import { useGetEnrollmentHYCSFundAccountData, useSubmitEnrollmentHYCSFundAccountData } from '@egw/api'
import { Alert, AlertType, Button, ButtonVariant, GenericError, Input, Link, ModalDrawer } from '@egw/components'
import { configClient } from '@egw/config'
import { DEFAULT_CURRENCY_CODE, WEALTH_ROUTES } from '@egw/constants'
import { Trans, useTranslation } from '@egw/i18n'
import { EnrollmentHYCSLayout } from '@egw/layouts'
import { Logger } from '@egw/logger'
import { addCentsToCurrency, formatInputToCurrency, removeCurrencySymbols } from '@egw/utils'
import { useCallback, useEffect, useMemo, useState, type FC } from 'react'
import { Controller, useForm } from 'react-hook-form'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'
import { sendEvent } from '@egw/events'
import { useAuth } from '@egw/auth'
import { ManualLink, PageHeader, PlaidLink } from '../../../components'
import { type Config } from '../../../config/config'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'
import { LinkingType } from '../../../types/account-type'
import { EDataLayerEventWealth } from '../../../constants/events/EDataLayerEventWealth'
import { ROUTES } from '../../../constants/routes/routes-hycs'

const log = new Logger('Enrollment - HYCS - FundAccount')

interface FormData {
  amount: string
  from: string
}

export const FundAccountPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()
  const config = configClient.getConfig() as Config
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [linkingType, setLinkingType] = useState<LinkingType>(LinkingType.NOT_SET)
  const [linkedAccountName, setLinkedAccountName] = useState<string | null>(null)
  const [transferError, setTransferError] = useState<boolean>(false)

  const { data, isLoading, error } = useGetEnrollmentHYCSFundAccountData()

  const { pingSession } = useAuth()

  const applicationId = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.applicationId : null

  useEffect(() => {
    if (!applicationId) return
    sendEvent(EDataLayerEventWealth.BANKHYCHYS_FUNDING_STARTED, { applicationId })
  }, [applicationId])

  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentHYCSFundAccountData()

  const unlinkAccount = useCallback(async () => {
    sendEvent(EDataLayerEventWealth.BANKHYCHYS_UNLINK_ACCOUNT, { applicationId })

    setIsOpen(true)

    const unlinkResponse = await mutate({ unlinkAccount: true })

    if (unlinkResponse?.__typename === 'SubmitEnrollmentSuccess') {
      setLinkedAccountName(null)
    }
  }, [mutate, applicationId])

  const toInputHandler = async () => {
    if (linkedAccountName) {
      await unlinkAccount()
    } else {
      setIsOpen(true)
    }
  }

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors: formErrors, isValid },
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      amount: '',
      from: '',
    },
  })

  const amountValue = watch('amount')

  useEffect(() => {
    setValue('from', linkedAccountName ?? '', { shouldValidate: false })
  }, [linkedAccountName, setValue])

  const fundLaterHandler = async (): Promise<void> => {
    const skipFunding = await mutate({ skipFunding: true })

    // On Skip funding, if enrollment is `complete`, we go to Dashboard
    if (skipFunding?.__typename === 'SubmitEnrollmentSuccess' && skipFunding.complete) {
      sendEvent(EDataLayerEventWealth.BANKHYCHYS_FUNDING_SKIP, { applicationId })
      void navigate(WEALTH_ROUTES.OVERVIEW.path)
    }
  }

  // Ping Session Now as User should theoretically be authenticated
  // This will ensure that if the user skips funding, they will reach Overview page
  useEffect(() => {
    void pingSession()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Protect route
  useValidateRoute(data?.path)

  const onSubmit = handleSubmit(async (fData: unknown): Promise<void> => {
    const formData = fData as FormData
    if (isValid) {
      sendEvent(EDataLayerEventWealth.BANKHYCHYS_FUNDING_SUBMIT, { applicationId })
      setTransferError(false)
      try {
        const fundingAmount = {
          amount: removeCurrencySymbols(formData.amount),
          currencyCode: DEFAULT_CURRENCY_CODE,
        }

        const achAuthTermsArray = config.zendeskArticles.achAuthorization.split('/')
        const id = `${achAuthTermsArray[achAuthTermsArray.length - 2]}/${achAuthTermsArray[achAuthTermsArray.length - 1]}`

        const response = await mutate({
          achAuthorization: {
            accepted: true,
            id,
          },
          fundingAmount,
          initiateFundsTransfer: true,
        })
        if (response?.__typename === 'SubmitEnrollmentSuccess' && response.complete) {
          sendEvent(EDataLayerEventWealth.BANKHYCHYS_TRANSFER_ACCEPTED)
          void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ROUTES.FUND_SUCCESS.path}`)
        } else {
          sendEvent(EDataLayerEventWealth.BANKHYCHYS_TRANSFER_REJECTED)
          setTransferError(true)
        }
      } catch (e) {
        log.error('Error submitting form', e)
      }
    }
  })

  // Add cents to amount if they are missing
  const addCents = (): void => {
    sendEvent(EDataLayerEventWealth.BANKHYCHYS_FUNDING_INTENT, { applicationId })

    const value = getValues('amount')

    if (value) {
      setValue('amount', addCentsToCurrency(value), { shouldValidate: true })
    }
  }

  const onClose = useCallback(() => {
    sendEvent(EDataLayerEventWealth.BANKHYCHYS_FUND_ACCOUNT_MODAL_CLOSED, { applicationId })
    setIsOpen(false)
  }, [applicationId])

  const modalTitle = useMemo((): string => {
    if (linkedAccountName) {
      return t('hycs.fund_account.account_linked')
    }

    if (linkingType === LinkingType.MANUAL) {
      return t('hycs.fund_account.manually_link_account')
    }

    return t('hycs.fund_account.link_funding_account')
  }, [linkedAccountName, linkingType, t])

  return (
    <EnrollmentHYCSLayout title={t('hycs.fund_account.page_title')}>
      <div className="egw-container-enroll-hycs h-full flex flex-col relative md:justify-center justify-start items-start">
        {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
        {isLoading ? (
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        ) : null}

        {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
        {!isLoading && !error ? (
          <>
            <form
              onSubmit={(e) => void onSubmit(e)}
              className="w-full flex-1 flex flex-col justify-start lg:justify-center"
            >
              <PageHeader title={t('hycs.fund_account.title')} />
              <p className="typo-primary-base-regular text-text-meta mt-md mb-xl">
                {t('hycs.fund_account.description')}
              </p>

              <div className="flex flex-col gap-sm">
                <Input
                  disabled
                  label={t('hycs.fund_account.to_label')}
                  value={t('hycs.fund_account.to_value')}
                  testId="to_input"
                />
                <Input
                  id="from"
                  value={linkedAccountName ?? ''}
                  onFocus={() => void toInputHandler()}
                  label={t('hycs.fund_account.from_label')}
                  readOnly
                  icon={
                    <p className="typo-primary-base-semibold text-text-action cursor-pointer">
                      {t(linkedAccountName ? 'hycs.fund_account.relink_account' : 'hycs.fund_account.link_account')}
                    </p>
                  }
                  className="cursor-pointer"
                  testId="btnLinkAccountModal_button"
                />
                <Controller
                  name="from"
                  control={control}
                  render={({ field }) => <input type="hidden" {...field} value={field.value as unknown as string} />}
                />

                <Controller
                  name="amount"
                  control={control}
                  rules={{
                    required: t('common.required_field'),
                    validate: {
                      maxAllowed: (value: string) => {
                        return (value && removeCurrencySymbols(value) <= 100000) || t('hycs.fund_account.max_amount')
                      },
                      minAllowed: (value: string) => {
                        return (value && removeCurrencySymbols(value) >= 25000) || t('hycs.fund_account.min_amount')
                      },
                    },
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      label={t('hycs.fund_account.amount_label')}
                      placeholder={amountValue ? undefined : t('hycs.fund_account.amount_placeholder')}
                      required
                      error={formErrors.amount?.message}
                      valueFormatter={(value) => formatInputToCurrency(value, true)}
                      onBlur={addCents}
                      inputMode="decimal"
                      testId="amount_input"
                      className="cursor-pointer"
                    />
                  )}
                />
              </div>

            {submitError ? <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" /> : null}

            {/* Transfer Error */}
            {transferError ? (
              <Alert alertType={AlertType.Error} description={t('hycs.fund_account.errors.fund_transfer')} className="mt-sm" />
            ) : null}

              {/* AUTO GENERATED COMPONENTS */}

              <div className="text-text-meta text-xs mt-md">
                <Trans
                  className="typo-primary-xs text-text-body"
                  t={t}
                  i18nKey="hycs.fund_account.ach_terms_and_conditions"
                >
                  <Link
                    href={config.zendeskArticles.achAuthorization}
                    className="hyperlink-muted underline inline-flex typo-primary-xs text-text-body hover:text-text-body"
                  />
                </Trans>
              </div>

              <div className="flex flex-col sm:flex-row gap-md">
                <Button
                  type="submit"
                  className="w-full md:w-min mt-xl"
                  isDisabled={!isValid}
                  isLoading={isSubmitting}
                  testId="primaryAction_button"
                >
                  {t('hycs.fund_account.transfer_funds')}
                </Button>

                <Button
                  variant={ButtonVariant.Secondary}
                  onClick={() => {
                    void fundLaterHandler()
                  }}
                  className="w-full md:w-min mt-xl"
                  testId="skipFundingAction_button"
                >
                  {t('hycs.fund_account.fund_later')}
                </Button>
              </div>
            </form>

            <ModalDrawer isOpen={isOpen} onClose={onClose} title={modalTitle}>
              {isOpen && linkingType !== LinkingType.MANUAL ? (
                <PlaidLink
                  applicationId={applicationId}
                  setLinkingType={setLinkingType}
                  onPlaidSuccess={setLinkedAccountName}
                  onClose={onClose}
                />
              ) : null}

              {linkingType === LinkingType.NOT_SET && (
                <div className="border-t border-light flex justify-center mt-md mb-2xs">
                  <span className="text-meta bg-white -mt-3 px-3">or</span>
                </div>
              )}

              {linkingType !== LinkingType.PLAID_INSTANT ? (
                <ManualLink
                  applicationId={applicationId}
                  onAccountLinkSuccess={setLinkedAccountName}
                  setLinkingType={setLinkingType}
                  onClose={onClose}
                />
              ) : null}
            </ModalDrawer>
          </>
        ) : null}

        {/* This error handles the case when the initial data is not loaded, for example network error */}
        {error ? (
          <div className="flex-1 flex flex-col justify-start md:justify-center">
            <GenericError errorMessage={t('common.generic_error')} />
          </div>
        ) : null}
      </div>
    </EnrollmentHYCSLayout>
  )
}
